import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { pathWithPreservedParams } from '../../routing/navigation.js';
import './Header.css';

/**
 * Header component for the Revetall application
 * @returns {JSX.Element} Header component
 */
const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut, companies, activeCompany, hasCompanyAccess, switchCompany } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLanguageHovered, setIsLanguageHovered] = useState(false);
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const companyDropdownRef = useRef(null);

  const handleLogout = async () => {
    await signOut();
  };

  const handleCompanySwitch = (company) => {
    if (company.id !== activeCompany?.id) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('company', String(company.id));
        next.delete('session_id');
        return next;
      }, { replace: true });
      switchCompany(company);
    }
    setIsCompanyDropdownOpen(false);
  };

  const handleCompanyDropdownClick = () => {
    setIsCompanyDropdownOpen(!isCompanyDropdownOpen);
  };

  const handleSettingsClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSettingsOption = () => {
    navigate(pathWithPreservedParams('/settings', searchParams));
    setIsDropdownOpen(false);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setIsDropdownOpen(false);
    setIsLanguageHovered(false);
  };

  const handleLogoutClick = () => {
    handleLogout();
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsLanguageHovered(false);
      }
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setIsCompanyDropdownOpen(false);
      }
    };

    if (isDropdownOpen || isCompanyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isCompanyDropdownOpen]);

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="logo">
          <div className="logo-container">
            <img src="/revetall-logo.svg" alt="Revetall" className="logo-with-wordmark" />
          </div>
        </div>
        
        <div className="header-info">
          {/* Company selector - only show if user has company access */}
          {hasCompanyAccess && activeCompany && (
            <div className="company-info">
              {companies.length > 1 ? (
                <div className="company-dropdown-container" ref={companyDropdownRef}>
                  <button 
                    className="company-selector"
                    onClick={handleCompanyDropdownClick}
                    type="button"
                  >
                    <span className="company-selector-text">{activeCompany.name}</span>
                  </button>
                  {isCompanyDropdownOpen && (
                    <div className="company-dropdown-menu">
                      {companies.map(company => (
                        <button
                          key={company.id}
                          className={`company-dropdown-item ${company.id === activeCompany.id ? 'active' : ''}`}
                          onClick={() => handleCompanySwitch(company)}
                        >
                          {company.name} {company.id === activeCompany.id ? '✓' : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <span className="company-name">{activeCompany.name}</span>
              )}
            </div>
          )}
          
          <div className="user-info">
            <span className="user-email">{user?.email}</span>
          </div>
          <div className="settings-dropdown-container" ref={dropdownRef}>
            <button 
              className="settings-selector"
              onClick={handleSettingsClick}
              type="button"
            >
              <span className="settings-selector-text">{t('nav.settings')}</span>
            </button>
            {isDropdownOpen && (
              <div className="settings-dropdown-menu">
                <button 
                  className="settings-dropdown-item"
                  onClick={handleSettingsOption}
                >
                  {t('nav.settings')}
                </button>
                <div 
                  className="settings-dropdown-item settings-dropdown-item-with-submenu"
                  onMouseEnter={() => setIsLanguageHovered(true)}
                  onMouseLeave={() => setIsLanguageHovered(false)}
                >
                  <span>Language</span>
                  <span className="submenu-arrow">›</span>
                  {isLanguageHovered && (
                    <div className="settings-submenu">
                      <button 
                        className={`settings-submenu-item ${i18n.language === 'no' ? 'active' : ''}`}
                        onClick={() => handleLanguageChange('no')}
                      >
                        🇳🇴 Norsk {i18n.language === 'no' ? '✓' : ''}
                      </button>
                      <button 
                        className={`settings-submenu-item ${i18n.language === 'en' ? 'active' : ''}`}
                        onClick={() => handleLanguageChange('en')}
                      >
                        🇬🇧 English {i18n.language === 'en' ? '✓' : ''}
                      </button>
                    </div>
                  )}
                </div>
                <button 
                  className="settings-dropdown-item"
                  onClick={handleLogoutClick}
                >
                  {t('nav.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
